/**
 * /wall-of-tenants — public showcase page for the live activity feed.
 *
 * v34.42 (Feb 2026): The widget version lives on the auth'd dashboard;
 * this is the marketing-facing counterpart that visitors hit from the
 * landing page "See live activity →" CTA and from the widget's own
 * "See all →" link.
 *
 * Reuses the same /api/wall/feed contract, but renders larger event
 * cards, aggregate KPIs, the "what counts as an event" explainer,
 * and a CTA to start a pilot or open the auth'd dashboard.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, Users, TrendingUp, Loader2, MapPin, ArrowRight,
  ShieldCheck, Eye, Sparkles, Zap, Clock,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { ENGINE_COUNT } from '../constants/product';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 8000;

const TONE = {
  savings: { dot: 'bg-emerald-400', text: 'text-emerald-300', ring: 'ring-emerald-500/30', glow: 'shadow-[0_0_20px_-6px_rgba(16,185,129,0.45)]' },
  policy:  { dot: 'bg-amber-400',   text: 'text-amber-300',   ring: 'ring-amber-500/30',   glow: 'shadow-[0_0_20px_-6px_rgba(245,158,11,0.40)]' },
  neutral: { dot: 'bg-sky-400',     text: 'text-sky-300',     ring: 'ring-sky-500/30',     glow: '' },
};

const relTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  return `${Math.round(diff / 3600)}h ago`;
};

const fmt$ = (n) => (typeof n === 'number' ? n.toLocaleString('en-US', { maximumFractionDigits: 0 }) : '—');

const WallOfTenantsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchFeed = async () => {
      try {
        const res = await fetch(`${API}/api/wall/feed?limit=30`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) { setData(json); setErr(null); }
      } catch (e) {
        if (!cancelled) setErr(e.message || 'feed unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchFeed();
    const t = setInterval(fetchFeed, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden">
      <SEOHead
        title="Wall of Tenants — live FinOps savings, this minute | CARTIEAI"
        description="Watch CARTIE save money for real teams, in real time. Anonymous, opt-in, refreshed every 8 seconds. The kind of social proof Apptio dashboards never let you see."
      />
      {/* Decorative background glows */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-32 left-1/3 w-[620px] h-[620px] rounded-full bg-emerald-500/[0.08] blur-[140px]" />
        <div className="absolute top-96 -right-32 w-[520px] h-[520px] rounded-full bg-sky-500/[0.06] blur-[120px]" />
      </div>

      {/* Top nav strip */}
      <header className="sticky top-0 z-30 bg-slate-950/85 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="wall-nav-home">
            <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-7 h-7 object-contain" />
            <span className="font-display font-black text-base tracking-tight">
              <span className="text-white">CARTIE</span><span className="text-emerald-400">AI</span>
              <sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super">™</sup>
            </span>
          </Link>
          <Link to="/" className="text-sm text-slate-400 hover:text-emerald-300 inline-flex items-center gap-1">
            ← Back home
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12 sm:py-20">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-5" data-testid="wall-live-pill">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="text-[11px] font-mono uppercase tracking-widest text-emerald-200">Wall of Tenants · Live</span>
            {data?.demo && (
              <span className="text-[10px] font-mono uppercase tracking-widest text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5 ml-1" data-testid="wall-demo-pill">
                demo
              </span>
            )}
          </div>
          <h1 className="text-4xl sm:text-6xl font-extrabold tracking-[-0.03em] leading-[1.05] mb-5">
            Watch CARTIE save money{' '}
            {/* v34.60 — was bg-clip-text gradient ending in sky-400 which faded
                the last letters of "for real teams." to look like glare. Solid
                bright emerald + drop-shadow glow that matches the brand. */}
            <span className="text-emerald-300 drop-shadow-[0_0_24px_rgba(16,185,129,0.45)]">
              for real teams.
            </span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Every 8 seconds we pull the latest engine actions across active tenants. Cache hits, retry storms caught, idle pods killed, prompt compaction wins, RDS rightsizes — the savings show up here the moment they land.
          </p>
        </div>

        {/* KPI row */}
        <div className="grid sm:grid-cols-3 gap-3 mb-8">
          <div className="rounded-xl border border-emerald-500/30 bg-slate-900 px-5 py-5">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-emerald-300 mb-2">
              <TrendingUp className="w-3.5 h-3.5" />
              Saved · last 7 min
            </div>
            <div className="font-display font-black text-3xl text-emerald-300 tabular-nums" data-testid="wall-kpi-saved">
              ${fmt$(data?.total_saved_today_usd)}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">aggregated across all active tenants</div>
          </div>
          <div className="rounded-xl border border-sky-500/30 bg-slate-900 px-5 py-5">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-sky-300 mb-2">
              <Users className="w-3.5 h-3.5" />
              Active tenants
            </div>
            <div className="font-display font-black text-3xl text-sky-300 tabular-nums" data-testid="wall-kpi-tenants">
              {data ? data.active_tenants : '—'}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">producing at least one event in the last hour</div>
          </div>
          <div className="rounded-xl border border-violet-500/30 bg-slate-900 px-5 py-5">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-violet-300 mb-2">
              <Activity className="w-3.5 h-3.5" />
              Events / minute
            </div>
            <div className="font-display font-black text-3xl text-violet-300 tabular-nums" data-testid="wall-kpi-rate">
              {data ? Math.max(1, Math.round((data.events?.length || 0) * 60 / 7)) : '—'}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">across {ENGINE_COUNT} engines · sliding 7-min window</div>
          </div>
        </div>

        {/* Feed */}
        <div className="rounded-2xl border border-white/10 bg-slate-900 overflow-hidden mb-12" data-testid="wall-feed">
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-slate-950">
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-[11px] font-mono uppercase tracking-widest text-slate-400">Recent activity · auto-refresh</span>
            </div>
            <span className="text-[10px] font-mono text-slate-500">poll · 8s</span>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-16 text-slate-500 text-xs gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading live feed…
            </div>
          )}
          {err && !data && (
            <div className="px-5 py-10 text-sm text-amber-300/80 text-center">
              Feed temporarily unavailable. Retrying every 8s…
            </div>
          )}
          {data && (
            <ul className="divide-y divide-white/[0.05] max-h-[640px] overflow-y-auto" data-testid="wall-events">
              {data.events.map((e) => {
                const tone = TONE[e.tone] || TONE.neutral;
                return (
                  <li
                    key={e.id}
                    className={`px-5 py-4 flex items-start gap-4 hover:bg-slate-950 transition-colors`}
                    data-testid={`wall-event-row-${e.id}`}
                  >
                    <span className={`shrink-0 mt-2 w-2 h-2 rounded-full ${tone.dot} ring-2 ${tone.ring} ${tone.glow}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap text-xs mb-1">
                        <span className="font-mono text-slate-200 font-semibold">{e.tenant}</span>
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-500 font-mono">
                          <MapPin className="w-2.5 h-2.5" />{e.region}
                        </span>
                        {e.engine && (
                          <span className="text-[10px] text-slate-500 font-mono">· {e.engine}</span>
                        )}
                        <span className="text-[10px] text-slate-600 font-mono ml-auto">{relTime(e.at)}</span>
                      </div>
                      <div className="text-sm text-slate-200 leading-snug">
                        {e.text}
                      </div>
                      {e.saved_usd > 0 && (
                        <div className={`mt-1.5 text-xs font-mono font-semibold ${tone.text}`}>
                          +${fmt$(e.saved_usd)} saved
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* What counts as an event */}
        <div className="grid md:grid-cols-3 gap-3 mb-12">
          <div className="rounded-xl border border-emerald-500/20 bg-slate-900 px-5 py-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-300">Savings event</span>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              An engine moved money: semantic cache hit, retry storm caught, zombie pod killed, RDS rightsized, egress rerouted. Saved-dollar amount is the conservative midpoint.
            </p>
          </div>
          <div className="rounded-xl border border-amber-500/20 bg-slate-900 px-5 py-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-amber-300">Policy event</span>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              An automated guardrail tripped: a PR was blocked for cost, a hot-pilot tenant got a throttle decision, a Slack channel hit its quota. No dollars saved — but no dollars lost either.
            </p>
          </div>
          <div className="rounded-xl border border-sky-500/20 bg-slate-900 px-5 py-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-sky-400" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-sky-300">Neutral event</span>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Tenant activity that's worth showing but doesn't move money: login, dashboard view, board-deck export. Helps confirm the platform is alive.
            </p>
          </div>
        </div>

        {/* Trust row */}
        <div className="grid md:grid-cols-3 gap-3 mb-12">
          <div className="rounded-xl border border-white/10 bg-slate-900 px-5 py-5">
            <Eye className="w-5 h-5 text-emerald-300 mb-2" />
            <div className="text-sm font-semibold text-white mb-1">Anonymous by default</div>
            <div className="text-xs text-slate-400 leading-relaxed">Tenant names are anonymised animal/element pairings (polar-otter, neon-fern, …). Region is rolled up to AWS region level. No PII ever surfaces.</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-slate-900 px-5 py-5">
            <ShieldCheck className="w-5 h-5 text-sky-300 mb-2" />
            <div className="text-sm font-semibold text-white mb-1">Opt-in only</div>
            <div className="text-xs text-slate-400 leading-relaxed">Customers explicitly toggle a "Show our wins on the Wall" switch in their org settings. SOC 2 evidence trail captures every consent flip.</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-slate-900 px-5 py-5">
            <Sparkles className="w-5 h-5 text-violet-300 mb-2" />
            <div className="text-sm font-semibold text-white mb-1">No marketing math</div>
            <div className="text-xs text-slate-400 leading-relaxed">Saved-dollar amounts come straight from the engine's own audit record — the same numbers your CFO sees on the customer P&L page.</div>
          </div>
        </div>

        {/* Demo-mode honesty banner */}
        {data?.demo && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-5 py-4 mb-12" data-testid="wall-demo-banner">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-semibold text-amber-200 mb-1">You're seeing demo data</div>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Until our HN-launch tenants flip the "show our wins on the Wall" switch, we render realistic simulated events from the actual engine catalog. The backend contract is identical to production — when consent rolls in, this page swaps to real data with zero frontend changes.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* CTA row */}
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm transition-colors"
            data-testid="wall-cta-pricing"
          >
            <Zap className="w-4 h-4" />
            Start a pilot
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/15 hover:border-white/30 text-slate-200 font-semibold text-sm transition-colors"
            data-testid="wall-cta-dashboard"
          >
            <Activity className="w-4 h-4" />
            Open the live dashboard <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>
    </div>
  );
};

export default WallOfTenantsPage;
